import {
  ClipboardCheck,
  QrCode,
  ScanLine,
  Package,
  ChevronRight,
  Globe,
  Clock,
  Users,
  Zap,
  Calendar,
  Gauge,
  ShieldCheck,
  BarChart3,
  Mail,
  Phone,
} from "lucide-react";
import { useTranslation } from "@refinedev/core";

const Checkin = () => {
  const { translate } = useTranslation();
  const benefits = [
    {
      icon: Gauge,
      title: translate("checkin.benefits.optimized.title"),
      subtitle: translate("checkin.benefits.optimized.subtitle"),
      description: translate("checkin.benefits.optimized.description"),
    },
    {
      icon: ShieldCheck,
      title: translate("checkin.benefits.reliable.title"),
      subtitle: translate("checkin.benefits.reliable.subtitle"),
      description: translate("checkin.benefits.reliable.description"),
    },
    {
      icon: BarChart3,
      title: translate("checkin.benefits.transparent.title"),
      subtitle: translate("checkin.benefits.transparent.subtitle"),
      description: translate("checkin.benefits.transparent.description"),
    },
  ];

  const steps = [
    {
      icon: ClipboardCheck,
      title: translate("checkin.steps.verify.title"),
      description: translate("checkin.steps.verify.description"),
    },
    {
      icon: QrCode,
      title: translate("checkin.steps.receiveQR.title"),
      description: translate("checkin.steps.receiveQR.description"),
    },
    {
      icon: ScanLine,
      title: translate("checkin.steps.presentQR.title"),
      description: translate("checkin.steps.presentQR.description"),
    },
    {
      icon: Package,
      title: translate("checkin.steps.complete.title"),
      description: translate("checkin.steps.complete.description"),
    },
  ];
  const stats = [
    {
      icon: Calendar,
      value: "100+",
      label: translate("checkin.stats.eventsServed.label"),
      description: translate("checkin.stats.eventsServed.description"),
    },
    {
      icon: Users,
      value: "20,000",
      label: translate("checkin.stats.largestEvent.label"),
      description: translate("checkin.stats.largestEvent.description"),
    },
    {
      icon: Zap,
      value: "2.3s",
      label: translate("checkin.stats.fastestSpeed.label"),
      description: translate("checkin.stats.fastestSpeed.description"),
    },
  ];

  const section1 = {
    icon: Globe,
    title: translate("checkin.section1.title"),
    description: translate("checkin.section1.description"),
  };

  const section2 = {
    icon: Clock,
    title: translate("checkin.section2.title"),
    description: translate("checkin.section2.description"),
  };

  return (
    <section>
      <div className="p-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl lg:text-4xl font-bold text-brand-600 mb-2">
            {translate("checkin.title")}
          </h1>
          <p className="text-muted-foreground mb-2">
            {translate("checkin.subtitle")}
          </p>
          <div className="w-16 h-1 bg-brand-600 mx-auto"></div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div>
            <div>
              <p className="leading-relaxed text-lg">
                {translate("checkin.intro")}
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="rounded-xl bg-white shadow-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-400 to-brand-500 flex items-center justify-center">
                    <benefit.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-foreground">
                      {benefit.title}
                    </h3>
                    <span className="text-sm font-semibold text-brand-500">
                      {benefit.subtitle}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 4-Step Process */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-2">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-1 items-center">
                <div className="flex-1 h-full rounded-xl shadow-lg bg-white">
                  <div className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-brand-400 to-brand-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-sm font-semibold text-brand-500 mb-1">
                      {translate("checkin.step", { step: index + 1 })}
                    </div>
                    <h3 className="font-bold text-foreground mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < 3 && (
                  <ChevronRight className="hidden lg:block shrink-0 w-8 h-8 text-brand-400 mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Two Highlight Sections */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="group rounded-xl shadow-lg overflow-hidden bg-gradient-to-br from-brand-400 to-blue-700 text-white hover:shadow-xl hover:shadow-blue-200/50 transition-all duration-300">
            <div className="p-8 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <section1.icon className="w-12 h-12 mb-4" />
              <h3 className="text-xl font-bold mb-3">{section1.title}</h3>
              <p className="text-blue-100">{section1.description}</p>
            </div>
          </div>

          <div className="group rounded-xl shadow-lg overflow-hidden bg-gradient-to-br from-brand-400 to-blue-700 text-white hover:shadow-xl hover:shadow-blue-200/50 transition-all duration-300">
            <div className="p-8 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <section2.icon className="w-12 h-12 mb-4" />
              <h3 className="text-xl font-bold mb-3">{section2.title}</h3>
              <p className="text-blue-100">{section2.description}</p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group rounded-xl bg-white shadow-lg hover:shadow-lg"
            >
              <div className="p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-magnolia-600 flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-300">
                  <stat.icon className="w-7 h-7 text-brand-500" />
                </div>
                <div className="text-4xl font-bold text-brand-600 mb-1">
                  {stat.value}
                </div>
                <div className="font-semibold text-foreground mb-1">
                  {stat.label}
                </div>
                <p className="text-sm text-muted-foreground">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <p className="text-center text-sm text-muted-foreground italic mb-6">
          {translate("checkin.note")}
        </p>
        <div className="bg-brand-600 rounded-xl shadow-lg p-8 text-center text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              {translate("checkin.cta.title")}
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {translate("checkin.cta.subtitle")}
            </p>

            {/* Contact Information */}
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-8">
                {translate("checkin.cta.contactTitle")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                <div className="bg-white bg-opacity-10 rounded-xl p-4">
                  <div className="flex items-start justify-start space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-3">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-blue-100 text-sm mb-1">
                        {translate("checkin.cta.email")}
                      </p>
                      <a
                        href="mailto:biz@actiup.net"
                        className="text-white text-base font-semibold hover:text-blue-100 transition-colors"
                      >
                        biz@actiup.net
                      </a>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="bg-white bg-opacity-10 rounded-xl p-4">
                  <div className="flex items-start justify-start space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-3">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-blue-100 text-sm mb-1">
                        {translate("checkin.cta.phone")}
                      </p>
                      <a
                        href="tel:1900638337"
                        className="block text-white text-base font-semibold hover:text-blue-100 transition-colors"
                      >
                        1900 63 83 37
                      </a>
                      <a
                        href="tel:0842222276"
                        className="text-white text-base font-semibold hover:text-blue-100 transition-colors"
                      >
                        0842 22 22 76
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkin;
